import { mapImageConstants } from '../../utils/imageMapper';

interface ImageWithDescriptionProps {
  content: Array<{
    image: string[];
    description: string;
    imagePosition?: 'left' | 'right';
  }>;
}

export default function ImageWithDescription({ content }: ImageWithDescriptionProps) {
  return (
    <div className="space-y-12">
      {content.map((item, index) => (
        <div key={index} className="space-y-6">
          {/* Mobile Layout: Images stacked, description below */}
          <div className="md:hidden space-y-4">
            {/* All images stacked */}
            <div className="grid grid-cols-1 gap-4">
              {item.image.map((imageConstant, imageIndex) => {
                const imageUrl = mapImageConstants({ image: imageConstant }).image;
                return (
                  <div key={imageIndex} className="relative group">
                    <img
                      src={imageUrl}
                      alt={`Content image ${imageIndex + 1}`}
                      className="w-full min-h-64 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                );
              })}
            </div>
            
            {/* Description below images on mobile */}
            <div className="px-4">
              <p className="text-lg text-gray-300 leading-relaxed">
                {item.description}
              </p>
            </div>
          </div>

          {/* Desktop Layout: Alternating left/right with description starting from second image */}
          <div className="hidden md:block space-y-8">
            {item.image.map((imageConstant, imageIndex) => {
              const imageUrl = mapImageConstants({ image: imageConstant }).image;
              const isEven = imageIndex % 2 === 0;
              const isFirstImage = imageIndex === 0;
              const isSecondImage = imageIndex === 1;
              const isSingleImage = item.image.length === 1;
              
              // For single images, use imagePosition if provided, otherwise default to left
              const shouldImageBeLeft = isSingleImage 
                ? (item.imagePosition === 'right' ? false : true)
                : isEven;
              
              return (
                <div key={imageIndex} className={`flex ${shouldImageBeLeft ? 'flex-row' : 'flex-row-reverse'} items-start gap-8`}>
                  {/* Image */}
                  <div className="flex-1 relative group">
                    <img
                      src={imageUrl}
                      alt={`Content image ${imageIndex + 1}`}
                      className="w-full min-h-80 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  
                  {/* Description - show for single image or from second image onwards */}
                  {(isSingleImage || isSecondImage) && (
                    <div className="flex-1 py-4">
                      <p className="text-xl text-gray-300 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  )}
                  
                  {/* Empty space for first image when there are multiple images */}
                  {isFirstImage && !isSingleImage && (
                    <div className="flex-1"></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
